<template>
  <div class="message-composer">
    <div class="text-caption font-weight-medium mb-1">
      {{ $t('messages.composer_title') }}
    </div>
    <div class="composer-wrap">
      <v-textarea
        ref="textarea"
        v-model="text"
        :placeholder="$t('messages.composer_placeholder')"
        rows="2"
        auto-grow
        density="compact"
        variant="outlined"
        hide-details
        @input="onInput"
        @keydown="onKeydown"
        @blur="onBlur"
      ></v-textarea>

      <v-list
        v-if="showMentions && filteredUsers.length"
        class="mention-list"
        density="compact"
        elevation="4"
      >
        <v-list-item
          v-for="(u, idx) in filteredUsers"
          :key="'mention-' + u.id"
          :active="idx === activeIndex"
          @mousedown.prevent="selectMention(u)"
        >
          <v-list-item-title class="text-body-2">
            @{{ u.username }}
            <span v-if="fullName(u)" class="text-caption text-grey">
              — {{ fullName(u) }}
            </span>
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </div>

    <div class="d-flex justify-end mt-2">
      <v-btn
        color="primary"
        size="small"
        :disabled="!text.trim() || sending"
        :loading="sending"
        @click="send"
      >
        {{ $t('messages.send') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MessageComposer',
  props: {
    assetId: { required: true },
  },
  data() {
    return {
      text: '',
      users: [],
      sending: false,
      showMentions: false,
      mentionQuery: '',
      mentionStart: -1,
      activeIndex: 0,
    };
  },
  computed: {
    filteredUsers() {
      const q = this.mentionQuery.toLowerCase();
      return this.users
        .filter((u) => u.username && u.username.toLowerCase().startsWith(q))
        .slice(0, 8);
    },
  },
  methods: {
    async loadUsers() {
      try {
        const users = await this.$api.user.all();
        this.users = users || [];
      } catch (e) {
        this.users = [];
      }
    },
    fullName(u) {
      return [u.first_name, u.last_name].filter(Boolean).join(' ');
    },
    caretPos() {
      const el = this.$refs.textarea && this.$refs.textarea.$el
        ? this.$refs.textarea.$el.querySelector('textarea')
        : null;
      return el ? el.selectionStart : this.text.length;
    },
    onInput() {
      this.detectMention();
    },
    detectMention() {
      const pos = this.caretPos();
      const upToCaret = this.text.slice(0, pos);
      const match = upToCaret.match(/@([A-Za-z0-9_.+-]*)$/);
      if (match) {
        this.mentionStart = pos - match[0].length;
        this.mentionQuery = match[1];
        this.showMentions = true;
        this.activeIndex = 0;
      } else {
        this.showMentions = false;
      }
    },
    onKeydown(e) {
      if (!this.showMentions || !this.filteredUsers.length) {
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.activeIndex = (this.activeIndex + 1) % this.filteredUsers.length;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.activeIndex =
          (this.activeIndex - 1 + this.filteredUsers.length) %
          this.filteredUsers.length;
      } else if (e.key === 'Enter') {
        e.preventDefault();
        this.selectMention(this.filteredUsers[this.activeIndex]);
      } else if (e.key === 'Escape') {
        this.showMentions = false;
      }
    },
    onBlur() {
      setTimeout(() => {
        this.showMentions = false;
      }, 150);
    },
    selectMention(user) {
      if (!user) {
        return;
      }
      const pos = this.caretPos();
      const before = this.text.slice(0, this.mentionStart);
      const after = this.text.slice(pos);
      this.text = `${before}@${user.username} ${after}`;
      this.showMentions = false;
    },
    async send() {
      const body = this.text.trim();
      if (!body) {
        return;
      }
      this.sending = true;
      try {
        const res = await this.$api.asset.addAssetMessage(this.assetId, body);
        if (res && res.error) {
          this.$store.dispatch(
            'snackbars/add_failed_snack',
            String(res.error),
          );
          return;
        }
        this.text = '';
        this.$emit('added', res);
        this.$store.dispatch('messages/fetch_inbox', {});
      } finally {
        this.sending = false;
      }
    },
  },
  mounted() {
    this.loadUsers();
  },
};
</script>

<style scoped>
.composer-wrap {
  position: relative;
}
.mention-list {
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  max-height: 220px;
  overflow-y: auto;
}
</style>
